import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { InvShell, InvStageChip } from './InvShell';
import type { InvTabId } from './InvShell';
import { INV_NUMBERS, INV_PROSPECTS, INV_PROSPECT_TILES } from '../../invisalign/mockData';
import type { InvStage } from '../../invisalign/types';

export interface InvProspectsViewProps {
  onSelectTab?: (tab: InvTabId) => void;
  onOpenSettings?: () => void;
  onHold: boolean;
  onSetHold?: (next: boolean) => void;
  onOpenPatient?: (id: string) => void;
  /** Opens the Record-the-answer / How-did-it-go modal for a prospect. */
  onLogConversation?: (id: string) => void;
  /** Pushes the chosen stage into the URL (?stage=) so a refresh keeps it. */
  onStageChange?: ((stage: string) => void) | undefined;
  initialStage?: string | undefined;
}

const STAGE_FILTERS: { key: InvStage | 'all'; label: string; count: number }[] = [
  { key: 'all', label: 'All', count: INV_NUMBERS.all },
  { key: 'worth-asking', label: 'Worth asking', count: INV_NUMBERS.worthAsking },
  { key: 'learning', label: 'Learning', count: INV_NUMBERS.learning },
  { key: 'ready', label: 'Ready', count: INV_NUMBERS.ready },
  { key: 'consult-booked', label: 'Consult booked', count: INV_NUMBERS.consultBooked },
  { key: 'asked-not-now', label: 'Asked — not now', count: INV_NUMBERS.askedNotNow },
];

/** The last column reads differently per stage (amendment 34 §3.1). */
function stageDetailOf(p: (typeof INV_PROSPECTS)[number]): string {
  return p.stageDetail ?? p.nextAppt;
}

export function InvProspectsView(props: InvProspectsViewProps) {
  const { onSelectTab, onOpenSettings, onHold, onSetHold, onOpenPatient, onLogConversation, onStageChange, initialStage } = props;
  const stage: InvStage | 'all' =
    initialStage && STAGE_FILTERS.some((f) => f.key === initialStage) ? (initialStage as InvStage) : 'all';
  const [signal, setSignal] = useState('all');
  const [query, setQuery] = useState('');

  const setStage = (next: InvStage | 'all') => {
    if (onStageChange) onStageChange(next === 'all' ? '' : next);
  };

  // Every distinct signal label, for the "Any signal" filter.
  const signalLabels = useMemo(() => {
    const set = new Set<string>();
    for (const p of INV_PROSPECTS) for (const s of p.signals) set.add(s.label);
    return [...set];
  }, []);

  const rows = useMemo(() => {
    let list = stage === 'all' ? INV_PROSPECTS : INV_PROSPECTS.filter((p) => p.stage === stage);
    if (signal !== 'all') list = list.filter((p) => p.signals.some((s) => s.label === signal));
    const q = query.trim().toLowerCase();
    if (q !== '') list = list.filter((p) => p.name.toLowerCase().includes(q));
    return list;
  }, [stage, signal, query]);

  const activeFilter = STAGE_FILTERS.find((f) => f.key === stage);
  const totalForStage = activeFilter?.count ?? INV_NUMBERS.all;

  return (
    <InvShell active="prospects" onSelectTab={onSelectTab} onOpenSettings={onOpenSettings} onHold={onHold} onSetHold={onSetHold}>
      {/* three tiles */}
      <div className="mt-5 grid grid-cols-3 gap-3 max-[700px]:grid-cols-1">
        <Tile n={String(INV_PROSPECT_TILES.asked)} label="asked so far" />
        <Tile n={String(INV_PROSPECT_TILES.comingIn)} label="coming in for a visit" />
        <Tile n={INV_PROSPECT_TILES.benefitValue} label="in unused ortho benefit" money />
      </div>

      <div className="mt-4 rounded-[10px] bg-[var(--brand-indigo-tint)] px-3 py-2 text-[12px] text-[var(--brand-indigo)]">
        A person asks first — ELVA only reaches the ones your team marked as worth asking.
      </div>

      {/* stage chips */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {STAGE_FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => { setStage(f.key); }}
            className={cn('inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 text-[12.5px]',
              f.key === stage ? 'border-[var(--brand-indigo-line)] bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>
            {f.label}<span className="font-[var(--font-mono)] font-bold">{f.count}</span>
          </button>
        ))}
      </div>

      {/* signal filter + search */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={signal} onChange={(e) => { setSignal(e.target.value); }} aria-label="Filter by signal"
          className="rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 py-1.5 text-[12.5px] text-[var(--text-secondary)] outline-none focus:border-[var(--brand-indigo)]">
          <option value="all">Any signal</option>
          {signalLabels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input value={query} onChange={(e) => { setQuery(e.target.value); }} placeholder="Search by name"
          className="w-[200px] rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--brand-indigo)]" />
        <span className="ml-auto text-[11.5px] text-[var(--text-muted)]">
          Showing {rows.length} of {totalForStage}
          {rows.length < totalForStage ? <button type="button" className="ml-1.5 font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { setSignal('all'); setQuery(''); }}>Show all {totalForStage}</button> : null}
        </span>
      </div>

      <div className="mt-2 overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className="grid grid-cols-[1.4fr_1.6fr_0.9fr_1fr_150px] gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          <span>Patient</span><span>Why they’re here</span><span>Ortho benefit</span><span>Where they are</span><span className="text-right">Stage</span>
        </div>
        {rows.map((p) => (
          <div key={p.id} className="grid grid-cols-[1.4fr_1.6fr_0.9fr_1fr_150px] items-center gap-x-3 border-t border-[var(--border-default)] px-4 py-3 text-[12.5px] first:border-0 hover:bg-[var(--surface-sunken)]">
            <button type="button" className="text-left" onClick={() => { onOpenPatient?.(p.id); }}>
              <span className="font-semibold text-[var(--text-primary)] hover:underline">{p.name}</span>
              <span className="block text-[11px] text-[var(--text-muted)]">{p.age} · patient since {p.since}</span>
            </button>
            <span className="flex flex-wrap gap-1">
              {p.signals.map((s, i) => (
                <span key={`${s.label}-${String(i)}`} className="inline-flex items-baseline gap-1 rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">
                  {s.label}{s.date !== undefined ? <span className="text-[var(--text-muted)]">· {s.date}</span> : null}
                </span>
              ))}
            </span>
            <span className="font-[var(--font-mono)] text-[var(--money-green)]">{p.orthoBenefit}</span>
            <span className="text-[var(--text-secondary)]">{stageDetailOf(p)}</span>
            <span className="flex items-center justify-end gap-2">
              <InvStageChip stage={p.stage} />
              <button type="button" className="whitespace-nowrap text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { onLogConversation?.(p.id); }}>Record the answer</button>
            </span>
          </div>
        ))}
        {rows.length === 0 ? <div className="px-4 py-8 text-center text-[12.5px] text-[var(--text-muted)]">No one in this stage right now.</div> : null}
      </div>
    </InvShell>
  );
}

function Tile({ n, label, money }: { n: string; label: string; money?: boolean }) {
  return (
    <div className="rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className={cn('font-[var(--font-mono)] text-[22px] font-bold tabular-nums', money ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>{n}</div>
      <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{label}</div>
    </div>
  );
}
